import React, {useRef} from 'react'
import {Box, Image} from '@chakra-ui/react'
import themeBreakpoints from '../../../theme/foundations/breakpoints'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useScrollToLoadVideo, useAnalytics} from '_customHooks'
import type {VideoSource} from '_utils'

interface VideoProps {
    alt?: string
    videoxl?: string
    videolg?: string
    videomd?: string
    videosm?: string
    videoxs?: string
    /**
     * Parametric sources (breakpoint × explicit format), generated by
     * updateCloudinaryPDVideoSources. When provided, these take precedence over the
     * videoxs/sm/md/lg/xl props: the component renders one <source> per entry (media+type)
     * and uses the last entry (universal fallback) as the <video> src.
     * See specs/cloudinary-video-integration.md §8.5.
     */
    sources?: VideoSource[]
    position?: 'absolute' | 'relative' | 'fixed' | 'static' | 'sticky'
    zIndex?: number
    objectFit?: 'cover' | 'contain'
    width?: string | {base?: string; sm?: string; md?: string; lg?: string}
    height?: string
    overflow?: string
    autoplay?: boolean
    muted?: boolean
    poster?: string
    loop?: boolean
    controls?: boolean
    isTileImage?: boolean
    isVideoModule?: boolean
    /**
     * Above-the-fold priority. When true, the video skips the scroll-based lazy load and
     * loads immediately with eager preload (for the main hero/LCP element). When false
     * (default), it is lazy-loaded via useScrollToLoadVideo with preload="none" so it never
     * impacts the initial page load / onload. Driven by isMainBanner at the module level.
     */
    isPriority?: boolean
    /**
     * Analytics label for the `video` event (SFRA `data-analyticstitle` equivalent).
     * When provided AND `controls` is true, the atom emits analytics on play/pause/progress
     * (25/50/75/100%). Mirrors the SFRA Cloudinary video player behaviour: interactive videos
     * (controls) are tracked; autoplay/background videos are not.
     */
    analyticsTitle?: string
    playVideofunc?: () => void
    onTimeUpdateFunc?: (event: React.SyntheticEvent<HTMLVideoElement>) => void
}

/**
 * @name Video
 *
 * @component
 *
 * @description
 * Renders a video with responsive sources and various styling options.
 *
 * @param {VideoProps} props - Properties object.
 * @param {string} props.alt - The alt text for the video.
 * @param {string} props.videoxs - The source URL for the extra small video.
 * @param {string} props.videosm - The source URL for the small video.
 * @param {string} props.videomd - The source URL for the medium video.
 * @param {string} props.videolg - The source URL for the large video.
 * @param {string} props.videoxl - The source URL for the extra large video.
 * @param {string} [props.overflow='visible'] - The overflow property of the video container.
 * @param {string} [props.width='100%'] - The width of the video container.
 * @param {string} [props.height='100%'] - The height of the video container.
 * @param {string} [props.autoplay=false] - The possibility to have a video play when loaded.
 * @param {string} [props.muted=false] - The possibility of having the video muted.
 * @param {string} props.poster - The source URL for an image while video loading.
 * @param {string} [props.controls=false] - The possibility of having the controls shown.
 * @param {string} [props.loop=false] - The possibility of having the video going in loop.
 * @param {string} [props.isTileImage=false] - Adds the styles to the video to be used as a tile image.
 * @param {string} [props.isVideoModule=false] - Adds the styles to the video to be used as a module video, allowing to have an initial space when loading
 *
 * @returns {JSX.Element} The rendered `Picture` component.
 */
export const Video: React.FC<VideoProps> = ({
    alt,
    videoxs,
    videosm,
    videomd,
    videolg,
    videoxl,
    sources,
    poster,
    loop = false,
    autoplay = false,
    muted = false,
    controls = false,
    isTileImage = false,
    overflow = 'visible',
    width = '100%',
    height = '100%',
    isVideoModule = false,
    isPriority = false,
    analyticsTitle,
    playVideofunc,
    onTimeUpdateFunc,
    ...props
}) => {
    // Player options rule (SFRA parity, applied centrally for ALL modules):
    // a video WITH controls must NOT autoplay — it shows the poster until the user presses play.
    // See specs/analytics_requirements_SFRA.md §16 and the editorial "Player options" verification doc.
    const effectiveAutoplay = controls ? false : autoplay

    const {breakpoints} = themeBreakpoints
    const targetVideo = useRef(null)
    const scrollReady = useScrollToLoadVideo(targetVideo)
    // Above-the-fold (main banner) loads immediately; everything else lazy-loads on scroll
    // proximity so it never blocks the initial page load / onload.
    const isReady = isPriority || scrollReady
    const {locale} = useMultiSite()
    const lang = locale.id.split('-')[0] || 'en'
    const hasParametricSources = Array.isArray(sources) && sources.length > 0
    const legacySource = videoxs || videosm || videomd || videolg || videoxl
    // For parametric sources the last entry (H.264 mp4, no media query) is the universal fallback
    // used for the <track> captions src. The <video> element has NO src attribute — the browser
    // selects from the <source> children. load() is called by useScrollToLoadVideo once they
    // are rendered, which forces the browser to evaluate and buffer the best match.
    const fallbackSrc = hasParametricSources ? sources![sources!.length - 1].src : legacySource

    // Video analytics (SFRA parity): only for interactive videos (controls=true) with a title.
    // Emits the `video` event on play/pause and at 25/50/75/100% progress milestones.
    // Autoplay/background videos are NOT tracked. See specs/cloudinary-video-integration.md §13.5.
    const {analyticsEventVideo} = useAnalytics()
    const trackVideo = controls && !!analyticsTitle
    const lastMilestoneRef = useRef(0)
    // Progress milestones emitted during playback (SFRA parity: the player fires `percentsplayed`
    // DURING playback, so 100% arrives BEFORE the end-of-playback `pause`). The monotonic guard
    // (m > lastMilestone) makes each milestone — 100% included — fire at most once per playback,
    // so the trailing timeupdate ticks near the end cannot re-emit 100%.
    const PROGRESS_MILESTONES = [25, 50, 75, 100]

    const emitVideo = (action: string) => {
        analyticsEventVideo({eventName: 'video', action, category: 'video', label: analyticsTitle})
    }

    const handleVideoPlay = (e: React.SyntheticEvent<HTMLVideoElement>) => {
        // New playback: reset the milestone tracker so a replay re-emits progress from scratch.
        lastMilestoneRef.current = 0
        if (trackVideo) emitVideo('play')
        playVideofunc?.()
    }

    const handleVideoPause = () => {
        // SFRA parity: the player's native `pause` is tracked as-is, including the automatic
        // pause fired at end of playback (which arrives AFTER progress - 100%, as in SFRA).
        if (trackVideo) emitVideo('pause')
    }

    const handleVideoTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
        if (trackVideo) {
            const el = e.currentTarget
            if (el.duration) {
                const percent = (el.currentTime / el.duration) * 100
                // Monotonic guard (m > last) ensures each milestone — 100% included — fires at most
                // once per playback, so the trailing timeupdate ticks near the end cannot repeat it.
                const milestone = PROGRESS_MILESTONES.find((m) => percent >= m && m > lastMilestoneRef.current)
                if (milestone) {
                    lastMilestoneRef.current = milestone
                    emitVideo(`progress - ${milestone}%`)
                }
            }
        }
        onTimeUpdateFunc?.(e)
    }

    return (
        <Box
            ref={targetVideo}
            overflow={overflow}
            top={0}
            width={width}
            height={height}
            pt={isTileImage ? '127.3%' : ''}
            {...props}
            position={isVideoModule ? 'static' : 'relative'}
            data-testid="video"
        >
            {isTileImage && (
                <Image
                    src={getAssetUrl('static/img/product_placeholder.jpg')}
                    alt={`${alt} - Placeholder`}
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    zIndex={-1}
                />
            )}
            <video
                autoPlay={isReady ? effectiveAutoplay : false}
                playsInline={isReady}
                muted={effectiveAutoplay || muted}
                controls={controls}
                loop={loop}
                preload={isPriority ? 'auto' : 'none'}
                style={{
                    width: '100%',
                    height: props.position === 'absolute' || props.objectFit === 'cover' ? '100%' : 'auto',
                    position: props.position ? props.position : 'unset',
                    top: props.position === 'absolute' ? '0' : 'unset',
                    left: props.position === 'absolute' ? '0' : 'unset',
                    right: props.position === 'absolute' ? '0' : 'unset',
                    bottom: props.position === 'absolute' ? '0' : 'unset',
                    zIndex: props.zIndex != null ? props.zIndex : '-1',
                    objectFit: props.objectFit ? props.objectFit : 'unset',
                }}
                poster={poster}
                src={isReady && !hasParametricSources ? legacySource : undefined}
                disablePictureInPicture
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onTimeUpdate={handleVideoTimeUpdate}
                onEnded={
                    loop
                        ? (e) => {
                              // Loop restart: Safari does not reliably honor the native `loop` attribute
                              // when sources are loaded via Range requests, so force the restart manually.
                              const v = e.currentTarget
                              v.currentTime = 0
                              try { v.play()?.catch(() => {}) } catch (_) { /* test env */ }
                          }
                        : undefined
                }
            >
                {isReady &&
                    hasParametricSources &&
                    sources!.map((s, i) => <source key={`${s.type}-${s.media ?? 'base'}-${i}`} media={s.media} type={s.type} src={s.src} />)}
                {isReady && !hasParametricSources && videoxl && <source media={`(min-width: ${breakpoints.xl})`} srcSet={videoxl} />}
                {isReady && !hasParametricSources && videolg && <source media={`(min-width: ${breakpoints.lg})`} srcSet={videolg || videoxl} />}
                {isReady && !hasParametricSources && videomd && <source media={`(min-width: ${breakpoints.md})`} srcSet={videomd || videolg || videoxl} />}
                {isReady && !hasParametricSources && videosm && <source media={`(min-width: ${breakpoints.sm})`} srcSet={videosm || videomd || videolg || videoxl} />}
                {isReady && !hasParametricSources && <source srcSet={legacySource} />}
                <track kind="captions" src={fallbackSrc} srcLang={lang} />
                {alt && alt}
            </video>
        </Box>
    )
}
